/**
 * @fileoverview Reactivate User form schema.
 * @description Client-side checks for the Reactivate User dialog, mirroring the backend's
 * ReactivateUserRequestDto: the role and reporting manager the user comes back with are both
 * required, a reason from the configuration's closed list is required, and a note of at most the
 * configured length is required for the reason OTHER and optional otherwise. Messages are
 * translation keys.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { z } from 'zod'

import { OTHER_REASON_CODE } from '@/features/users/constants/lifecycle.constants'

/**
 * @description Builds the Reactivate User schema.
 * @param {{ noteMaxLength: number }} context - The longest a reason note may be.
 * @returns {z.ZodType} The form schema.
 */
export function buildReactivateUserFormSchema({ noteMaxLength }: { noteMaxLength: number }) {
  return z
    .object({
      roleId: z.string().min(1, 'sales.userManagement.reactivateUser.errors.roleRequired'),
      reportingManagerUserId: z
        .string()
        .min(1, 'sales.userManagement.reactivateUser.errors.reportsToRequired'),
      reasonCode: z.string().min(1, 'sales.userManagement.lifecycle.errors.reasonRequired'),
      reasonNote: z
        .string()
        .trim()
        .max(noteMaxLength, 'sales.userManagement.lifecycle.errors.noteTooLong'),
    })
    .refine((values) => values.reasonCode !== OTHER_REASON_CODE || values.reasonNote !== '', {
      message: 'sales.userManagement.lifecycle.errors.noteRequired',
      path: ['reasonNote'],
    })
}

export type ReactivateUserFormValues = z.infer<ReturnType<typeof buildReactivateUserFormSchema>>
